import { useEffect, useRef, useState, type KeyboardEvent as ReactKeyboardEvent, type MutableRefObject } from 'react';
import { api } from '../../api/client';
import { useNotify } from '../../components/notifications';
import { useConfirm } from '../../components/confirm';
import { markUserEdit } from '../../hooks/useUnsavedGuard';
import { ExportMenu } from '../../components/AppToolbar/ExportMenu';
import { useHotkeys, formatCombo } from '../../components/hotkeys';
import { TestMethodPickerDialog } from './TestMethodPickerDialog';
import {
  MSG_BAD_AMOUNT, MSG_DUPLICATE_TEST, MSG_ENTER_MATRIX,
  cleanAmountInput, editableAmount, fmtAmount, fromApiRow, normalizeAmount, stepAmount,
  toPayloadRow, validateTestRows,
  type ApiTestRow, type BufferedChildHandle, type TestMethodOption, type TestRow,
} from './itemTestsModel';
import './ItemTestsGrid.css';

const BASE = '/api/v1/assign-tests-to-item';

interface UnitOption { id: string; name: string }

const PAGE_SIZES = [10, 20, 50, 100];

/**
 * The "Tests" tab of the Assign Tests To Item detail (legacy Items_DetailView_NN →
 * Items_Linkparameters_ListView): the item's dbo.ItemTestMethodLink rows.
 *
 * Legacy parity: Add appends a blank row, the pencil edits ONE row inline (Matrix
 * is picked in the "Test Method" popup; Test and Method follow from it and are
 * read-only; Amount; Units), Delete drops the ticked rows. Nothing is written until
 * the detail's Save — the rows are buffered here and handed to the page through
 * `handleRef`, so Cancel really does discard them.
 */
export function ItemTestsGrid({ itemId, viewOnly, reloadToken, handleRef, onDirty }: {
  /** The item being edited. */
  itemId: string | null;
  viewOnly: boolean;
  /** Changes after every successful save — re-read the committed rows. */
  reloadToken: number;
  handleRef: MutableRefObject<BufferedChildHandle | null>;
  /** A buffered change the page's unsaved-changes guard must know about. */
  onDirty: () => void;
}) {
  const notify = useNotify();
  const { confirm } = useConfirm();

  const [rows, setRows] = useState<TestRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  // Row-inline editor (legacy InlineEditMode="Inline"): one row at a time.
  const [editKey, setEditKey] = useState<string | null>(null);
  const [draft, setDraft] = useState<TestRow | null>(null);
  const [draftIsNew, setDraftIsNew] = useState(false);
  const [testMethods, setTestMethods] = useState<TestMethodOption[]>([]);
  const [units, setUnits] = useState<UnitOption[]>([]);
  const [lookupsLoading, setLookupsLoading] = useState(false);
  // The "Test Method" popup behind the open row's Matrix cell.
  const [pickerOpen, setPickerOpen] = useState(false);
  const matrixRef = useRef<HTMLInputElement>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const newSeq = useRef(0);
  // Maximize/Restore (BO UI Standard, user request 2026-09-23 — "every detail
  // view grid" gets the Export + Maximize cluster top-right, AppDataGrid parity;
  // implemented locally since this is a bespoke buffered table, not <AppDataGrid>).
  const [maximized, setMaximized] = useState(false);
  useEffect(() => {
    if (!maximized) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setMaximized(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [maximized]);

  const closeEditor = () => { setEditKey(null); setDraft(null); setDraftIsNew(false); setPickerOpen(false); };

  useEffect(() => {
    let alive = true;
    setEditKey(null); setDraft(null); setDraftIsNew(false); setPickerOpen(false);
    setSelected(new Set());
    setPage(1);
    if (!itemId) {
      setRows([]);
      return () => { alive = false; };
    }
    setLoading(true);
    api.get<{ data?: ApiTestRow[] }>(`${BASE}/${itemId}/tests`)
      .then(({ data }) => { if (alive) setRows((data.data ?? []).map(fromApiRow)); })
      .catch(() => { if (alive) notify.error('Could not load the tests of this item.'); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [itemId, reloadToken, notify]);

  // Back to read-only: an editor still open has nothing left to commit to.
  useEffect(() => {
    if (viewOnly) { setEditKey(null); setDraft(null); setDraftIsNew(false); setPickerOpen(false); }
  }, [viewOnly]);

  // The lookups only matter while editing — fetched the first time Edit is pressed.
  const lookupsLoaded = useRef(false);
  useEffect(() => {
    if (viewOnly || lookupsLoaded.current) return;
    lookupsLoaded.current = true;
    setLookupsLoading(true);
    Promise.all([
      api.get<{ data?: TestMethodOption[] }>(`${BASE}/test-methods`),
      api.get<{ data?: UnitOption[] }>(`${BASE}/units`),
    ])
      .then(([tm, u]) => { setTestMethods(tm.data.data ?? []); setUnits(u.data.data ?? []); })
      .catch(() => {
        lookupsLoaded.current = false;
        notify.error('Could not load the Test and Units lists.');
      })
      .finally(() => setLookupsLoading(false));
  }, [viewOnly, notify]);

  // Save reads the LATEST rows — including a row still open in the editor, which
  // XAF also commits on Save — without the handle going stale between renders.
  const live = useRef({ rows, editKey, draft });
  useEffect(() => { live.current = { rows, editKey, draft }; }, [rows, editKey, draft]);
  useEffect(() => {
    const effective = () => {
      const { rows: list, editKey: key, draft: open } = live.current;
      return key && open ? list.map((r) => (r.key === key ? open : r)) : list;
    };
    handleRef.current = {
      validate: () => validateTestRows(effective()),
      payload: () => ({ tests: effective().map(toPayloadRow) }),
    };
    return () => { handleRef.current = null; };
  }, [handleRef]);

  const editing = editKey !== null;
  const patchDraft = (patch: Partial<TestRow>) => setDraft((d) => (d ? { ...d, ...patch } : d));

  // The Matrix cell has no native input the unsaved-changes tracker can watch, so a
  // pick / clear announces itself (standing rule 15).
  const pickTestMethod = (o: TestMethodOption) => {
    patchDraft({ testMethodOid: o.id, matrix: o.matrix, test: o.test, method: o.methodName });
    markUserEdit(matrixRef.current);
    setPickerOpen(false);
    requestAnimationFrame(() => matrixRef.current?.focus());
  };
  const clearTestMethod = () => {
    patchDraft({ testMethodOid: '', matrix: '', test: '', method: '' });
    markUserEdit(matrixRef.current);
  };

  const add = () => {
    if (editing || !itemId) return;
    newSeq.current += 1;
    const row: TestRow = {
      key: `new-${newSeq.current}`, oid: null, testMethodOid: '', matrix: '', test: '',
      method: '', amount: '', unitOid: '', unitName: '',
    };
    setRows((s) => [...s, row]);
    setEditKey(row.key);
    setDraft(row);
    setDraftIsNew(true);
    setPage(Math.max(1, Math.ceil((rows.length + 1) / pageSize)));
  };

  const startEdit = (r: TestRow) => {
    if (editing) return;
    setEditKey(r.key);
    setDraft({ ...r });
    setDraftIsNew(false);
  };

  const applyEdit = () => {
    if (!draft || !editKey) return;
    if (!draft.testMethodOid) { notify.warning(MSG_ENTER_MATRIX); return; }
    if (rows.some((r) => r.key !== editKey && r.testMethodOid === draft.testMethodOid)) {
      notify.warning(MSG_DUPLICATE_TEST);
      return;
    }
    if (draft.amount.trim() !== '' && !Number.isFinite(Number(draft.amount))) {
      notify.warning(MSG_BAD_AMOUNT);
      return;
    }
    const applied = draft;
    setRows((s) => s.map((r) => (r.key === editKey ? applied : r)));
    closeEditor();
    onDirty();
  };

  const cancelEdit = () => {
    // A row that was only just added and never applied is dropped again.
    if (draftIsNew && editKey) {
      const key = editKey;
      setRows((s) => s.filter((r) => r.key !== key));
    }
    closeEditor();
  };

  const removeSelected = async () => {
    if (editing || selected.size === 0) return;
    const ok = await confirm({
      title: 'Delete',
      message: 'You are about to delete the selected record(s). Do you want to proceed?',
      confirmLabel: 'Delete',
      tone: 'danger',
    });
    if (!ok) return;
    const gone = selected;
    setRows((s) => s.filter((r) => !gone.has(r.key)));
    setSelected(new Set());
    onDirty();
  };

  const onEditorKey = (e: ReactKeyboardEvent<HTMLInputElement | HTMLSelectElement>) => {
    if (e.key === 'Enter') { e.preventDefault(); applyEdit(); }
    else if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); cancelEdit(); }
  };

  const totalPages = Math.max(1, Math.ceil(rows.length / pageSize));
  const curPage = Math.min(page, totalPages);
  const pageRows = rows.slice((curPage - 1) * pageSize, curPage * pageSize);
  const pageAllChecked = pageRows.length > 0 && pageRows.every((r) => selected.has(r.key));
  const toggleRow = (key: string) => setSelected((s) => {
    const n = new Set(s);
    if (n.has(key)) n.delete(key); else n.add(key);
    return n;
  });
  const togglePage = () => setSelected((s) => {
    const n = new Set(s);
    if (pageAllChecked) pageRows.forEach((r) => n.delete(r.key)); else pageRows.forEach((r) => n.add(r.key));
    return n;
  });
  const colSpan = viewOnly ? 5 : 7;

  // Keyboard shortcuts for this child grid's toolbar (standing rule 14) — each
  // mirrors its button's enabled state.
  useHotkeys([
    { combo: 'alt+n', label: 'Add', group: 'Tests', order: 1, disabled: viewOnly || editing || !itemId, run: () => add() },
    { combo: 'alt+d', label: 'Delete', group: 'Tests', order: 2, disabled: viewOnly || editing || selected.size === 0, run: () => void removeSelected() },
  ]);

  return (
    <div className={`sd-assoc itl${maximized ? ' itl-maximized' : ''}`}>
      <div className="sd-assoc-tabbar" role="tablist">
        <span className="sd-assoc-tab active" role="tab" aria-selected="true">
          <i className="ti ti-settings" /> Tests
        </span>
      </div>
      <div className="sd-assoc-panel">
        <div className="sd-assoc-toolbar">
          {!viewOnly && (
            <>
              <button type="button" className="ca-btn" disabled={editing || !itemId} onClick={add}
                title={`Add (${formatCombo('alt+n')})`}>
                <span className="tb-i tb-i-green">＋</span> Add
              </button>
              <button type="button" className="ca-btn" disabled={editing || selected.size === 0}
                onClick={() => void removeSelected()} title={`Delete (${formatCombo('alt+d')})`}>
                <span className="tb-i tb-i-red">✕</span> Delete
              </button>
            </>
          )}
          {/* Export + Maximize — the shared top-right cluster every detail-view
              grid carries (AppDataGrid parity, user request 2026-09-23). Export
              acts on the FULL buffered row set (not just the visible page). */}
          <span className="adg-header-sp" />
          <ExportMenu
            iconOnly
            triggerClassName="adg-max-btn"
            filename="AssignTestsToItem-Tests"
            headers={['Matrix', 'Test', 'Method', 'Amount', 'Units']}
            rows={rows.map((r) => [r.matrix, r.test, r.method, fmtAmount(r.amount), r.unitName])}
          />
          <button
            type="button"
            className="adg-max-btn"
            onClick={() => setMaximized((m) => !m)}
            title={maximized ? 'Restore (Esc)' : 'Maximize'}
            aria-label={maximized ? 'Restore' : 'Maximize'}
          >
            <i className={`ti ${maximized ? 'ti-arrows-minimize' : 'ti-arrows-maximize'}`} aria-hidden="true" />
          </button>
        </div>
        <div className="sd-assoc-grid">
          <table>
            <thead>
              <tr>
                {!viewOnly && (
                  <th className="sd-assoc-cb">
                    <input type="checkbox" checked={pageAllChecked} disabled={editing || pageRows.length === 0}
                      onChange={togglePage} aria-label="Select all on page" />
                  </th>
                )}
                {!viewOnly && <th className="itl-act" aria-label="Edit" />}
                <th>Matrix</th>
                <th>Test</th>
                <th>Method</th>
                <th className="itl-num">Amount</th>
                <th>Units</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={colSpan} className="sd-assoc-empty">Loading…</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={colSpan} className="sd-assoc-empty">No data to display</td></tr>
              ) : pageRows.map((r) => {
                const open = r.key === editKey && draft ? draft : null;
                const shown = open ?? r;
                return (
                  <tr key={r.key} className={open ? 'itl-editing' : undefined}>
                    {!viewOnly && (
                      <td className="sd-assoc-cb">
                        <input type="checkbox" checked={selected.has(r.key)} disabled={editing}
                          onChange={() => toggleRow(r.key)} aria-label="Select row" />
                      </td>
                    )}
                    {!viewOnly && (
                      <td className="itl-act">
                        {/* Icon-only on purpose: a visible "Cancel" caption would read as
                            the page's own Cancel to the unsaved-changes guard. */}
                        {open ? (
                          <>
                            <button type="button" className="itl-ibtn itl-ok" title="Update" aria-label="Update"
                              onClick={applyEdit}><i className="ti ti-check" /></button>
                            <button type="button" className="itl-ibtn itl-cancel" title="Cancel" aria-label="Cancel"
                              onClick={cancelEdit}><i className="ti ti-x" /></button>
                          </>
                        ) : (
                          <button type="button" className="itl-ibtn itl-edit" title="Edit" aria-label="Edit"
                            disabled={editing} onClick={() => startEdit(r)}><i className="ti ti-edit" /></button>
                        )}
                      </td>
                    )}
                    <td>
                      {open ? (
                        // Legacy lookup editor: the matrix in a box, with Clear and Find
                        // buttons; Find (or a click / Enter on the box) opens the popup.
                        <div className="itl-find">
                          <input ref={matrixRef} type="text" readOnly value={open.matrix} placeholder="N/A"
                            className={`sd-input itl-find-in${open.testMethodOid ? '' : ' required-empty'}`}
                            aria-label="Matrix" title="Click to find a test method"
                            onClick={() => setPickerOpen(true)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === 'F4' || (e.altKey && e.key === 'ArrowDown')) {
                                e.preventDefault();
                                setPickerOpen(true);
                              } else if (e.key === 'Escape') {
                                e.preventDefault(); e.stopPropagation(); cancelEdit();
                              }
                            }} />
                          <button type="button" className="itl-find-btn" title="Clear" aria-label="Clear matrix"
                            disabled={!open.testMethodOid} onClick={clearTestMethod}><i className="ti ti-eraser" /></button>
                          <button type="button" className="itl-find-btn itl-find-go" title="Find" aria-label="Find test method"
                            onClick={() => setPickerOpen(true)}><i className="ti ti-search" /></button>
                        </div>
                      ) : r.matrix}
                    </td>
                    <td>{shown.test}</td>
                    <td>{shown.method}</td>
                    <td className="itl-num">
                      {open ? (
                        <AmountEditor value={open.amount} onChange={(amount) => patchDraft({ amount })}
                          onKeyDown={onEditorKey} />
                      ) : fmtAmount(r.amount)}
                    </td>
                    <td>
                      {open ? (
                        <select className="sd-input itl-in" value={open.unitOid} aria-label="Units"
                          onChange={(e) => {
                            const u = units.find((x) => x.id === e.target.value);
                            patchDraft({ unitOid: e.target.value, unitName: u?.name ?? '' });
                          }}
                          onKeyDown={onEditorKey}>
                          <option value="">N/A</option>
                          {/* Keep a unit the list no longer offers visible, not blank. */}
                          {open.unitOid && !units.some((u) => u.id === open.unitOid) && (
                            <option value={open.unitOid}>{open.unitName || open.unitOid}</option>
                          )}
                          {units.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                        </select>
                      ) : r.unitName}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {rows.length > 0 && (
          <div className="sd-assoc-foot">
            <span>Page {curPage} of {totalPages} ({rows.length} items)</span>
            <span className="sd-assoc-pager">
              <button type="button" disabled={curPage <= 1} aria-label="Previous page"
                onClick={() => setPage(curPage - 1)}>‹</button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((n) => (
                <button type="button" key={n} className={n === curPage ? 'sd-assoc-pg-active' : ''}
                  onClick={() => setPage(n)}>{n}</button>
              ))}
              <button type="button" disabled={curPage >= totalPages} aria-label="Next page"
                onClick={() => setPage(curPage + 1)}>›</button>
            </span>
            <span className="sd-assoc-size">
              Page size:
              <select value={pageSize} aria-label="Page size" data-unsaved-ignore
                onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}>
                {PAGE_SIZES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </span>
          </div>
        )}
      </div>

      <TestMethodPickerDialog
        open={pickerOpen && draft !== null}
        options={testMethods}
        loading={lookupsLoading}
        currentId={draft?.testMethodOid ?? ''}
        size24
        onPick={pickTestMethod}
        onClose={() => setPickerOpen(false)}
      />
    </div>
  );
}

/**
 * The Amount editor of the open row — the legacy XAF currency spin editor: it reads
 * "$0.00" (a blank amount is 0, as XPO loads it), edits as a plain number (focus
 * selects it, so typing replaces it), and ▲ / ▼ or ArrowUp / ArrowDown step by 1.
 */
function AmountEditor({ value, onChange, onKeyDown }: {
  value: string;
  onChange: (amount: string) => void;
  /** The row editor's Enter (Update) / Escape (Cancel). */
  onKeyDown: (e: ReactKeyboardEvent<HTMLInputElement>) => void;
}) {
  const [focused, setFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // A spin is not a native edit, so it announces itself to the unsaved-changes
  // guard (standing rule 15) — typing is picked up by the guard on its own.
  const spin = (delta: number) => {
    onChange(stepAmount(value, delta));
    markUserEdit(inputRef.current);
  };

  return (
    <div className="itl-money">
      <input ref={inputRef} type="text" inputMode="decimal" aria-label="Amount"
        className="sd-input itl-in itl-money-in"
        value={focused ? value : fmtAmount(value)}
        onFocus={(e) => {
          setFocused(true);
          onChange(editableAmount(value));
          // After React swaps "$0.00" for "0.00" — selecting now would select the old text.
          const box = e.currentTarget;
          setTimeout(() => box.select(), 0);
        }}
        onBlur={() => { setFocused(false); onChange(normalizeAmount(value)); }}
        onChange={(e) => onChange(cleanAmountInput(e.target.value))}
        onKeyDown={(e) => {
          if (e.key === 'ArrowUp') { e.preventDefault(); spin(1); }
          else if (e.key === 'ArrowDown') { e.preventDefault(); spin(-1); }
          else onKeyDown(e);
        }} />
      <span className="itl-spin">
        <button type="button" tabIndex={-1} title="Increase" aria-label="Increase amount"
          onMouseDown={(e) => e.preventDefault()} onClick={() => spin(1)}>▲</button>
        <button type="button" tabIndex={-1} title="Decrease" aria-label="Decrease amount"
          onMouseDown={(e) => e.preventDefault()} onClick={() => spin(-1)}>▼</button>
      </span>
    </div>
  );
}
